/**
 * WidgetTable — sortable table. Accepts either an array of row objects or
 * the common shape `[{ bucket, metric }, ...]` and renders the columns
 * declared on the widget (falling back to the row keys if columns are
 * empty or missing in the data).
 */

import { useMemo, useState } from 'react'

import type { TableWidget } from '../types'

interface WidgetTableProps {
  widget: TableWidget
  data?: unknown
  error?: string
}

type Row = Record<string, unknown>

function normalize(data: unknown): Row[] {
  if (!Array.isArray(data)) return []
  return data.filter((r): r is Row => !!r && typeof r === 'object')
}

export function WidgetTable({ widget, data, error }: WidgetTableProps) {
  const rows = useMemo(() => normalize(data), [data])
  const columns = useMemo(() => {
    if (widget.columns?.length) return widget.columns
    if (rows.length > 0) return Object.keys(rows[0]).slice(0, 8)
    return []
  }, [widget.columns, rows])
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const sorted = useMemo(() => {
    if (!sortKey) return rows
    return [...rows].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av
      }
      return sortDir === 'asc'
        ? String(av ?? '').localeCompare(String(bv ?? ''))
        : String(bv ?? '').localeCompare(String(av ?? ''))
    })
  }, [rows, sortKey, sortDir])

  function onHeaderClick(col: string) {
    if (sortKey === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(col)
      setSortDir('desc')
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="border-b border-slate-200 px-3 py-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
        {widget.title}
      </div>
      {error || rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-xs text-slate-400">
          {error ?? 'no data'}
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    scope="col"
                    className="cursor-pointer select-none px-3 py-2 text-left font-medium"
                    onClick={() => onHeaderClick(col)}
                  >
                    {col}
                    {sortKey === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, widget.limit).map((row, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700/60">
                  {columns.map((col) => (
                    <td key={col} className="px-3 py-1.5 text-slate-700 dark:text-slate-200">
                      {row[col] == null ? '—' : String(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
